import moment from "moment";
import { useSyncExternalStore } from "react";

export type TimeFormat = "12h" | "24h";

const STORAGE_KEY = "backoffice-time-format";
const CHANGE_EVENT = "backoffice-time-format-change";
const DEFAULT_TIME_FORMAT: TimeFormat = "24h";
const SUPPORTED_DATE_FORMATS = [
  moment.ISO_8601,
  "DD.MM.YYYY HH:mm",
  "DD.MM.YYYY HH:mm:ss",
  "DD.MM.YYYY hh:mm A",
  "DD.MM.YYYY hh:mm:ss A",
  "HH:mm",
  "HH:mm:ss",
  "hh:mm A",
  "hh:mm:ss A",
];

const parseDate = (value: string) => {
  const parsedDate = moment(value, SUPPORTED_DATE_FORMATS, true);

  if (parsedDate.isValid()) return parsedDate;

  // Some finance endpoints return JavaScript date strings such as
  // "Tue Aug 11 2026 07:24:26 GMT+0000 (Coordinated Universal Time)".
  // Parse those through the native Date implementation without relaxing the
  // strict parsing used for the formats above.
  return moment(new Date(value));
};

const getTimeFormat = (): TimeFormat => {
  if (typeof window === "undefined") return DEFAULT_TIME_FORMAT;

  return window.localStorage.getItem(STORAGE_KEY) === "12h" ? "12h" : "24h";
};

const subscribe = (callback: () => void) => {
  window.addEventListener("storage", callback);
  window.addEventListener(CHANGE_EVENT, callback);

  return () => {
    window.removeEventListener("storage", callback);
    window.removeEventListener(CHANGE_EVENT, callback);
  };
};

export const setTimeFormat = (format: TimeFormat) => {
  window.localStorage.setItem(STORAGE_KEY, format);
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const useTimeFormat = () =>
  useSyncExternalStore(subscribe, getTimeFormat, () => DEFAULT_TIME_FORMAT);

export const getDatePickerFormats = (timeFormat: TimeFormat) => ({
  timeFormat: timeFormat === "12h" ? "hh:mm aa" : "HH:mm",
  dateFormat:
    timeFormat === "12h" ? "dd.MM.yyyy hh:mm aa" : "dd.MM.yyyy HH:mm",
  placeholder:
    timeFormat === "12h" ? "DD.MM.YYYY hh:mm AM/PM" : "DD.MM.YYYY HH:mm",
});

export const formatDateTime = (
  value: string,
  timeFormat: TimeFormat,
  withSeconds = false,
) => {
  const timePattern =
    timeFormat === "24h"
      ? `HH:mm${withSeconds ? ":ss" : ""}`
      : `hh:mm${withSeconds ? ":ss" : ""} A`;

  const date = parseDate(value);

  return date.isValid() ? date.format(`DD.MM.YYYY ${timePattern}`) : value;
};

export const formatDate = (value: string) => {
  const date = parseDate(value);

  return date.isValid() ? date.format("DD.MM.YYYY") : value;
};

export const formatTime = (
  value: string,
  timeFormat: TimeFormat,
  withSeconds = false,
) => {
  const timePattern =
    timeFormat === "24h"
      ? `HH:mm${withSeconds ? ":ss" : ""}`
      : `hh:mm${withSeconds ? ":ss" : ""} A`;

  const date = parseDate(value);

  return date.isValid() ? date.format(timePattern) : value;
};
